// =============================================================================
// SETTINGS 2FA COMPONENT
// =============================================================================
// Ubicación: src/components/TwoFactor/Settings2FA.tsx
// =============================================================================

import React, { useState } from 'react';
import { Switch, Button, Modal, Typography, Space, Alert, Form } from 'antd';
import { message } from '../../services/toastMessage';
import { SafetyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { disable2FA } from '../../services/AuthenticationService';
import Enable2FAModal from './Enable2FAModal';
import FloatingLabelInput from '../FloatingLabelInput';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Text, Paragraph } = Typography;
const { confirm } = Modal;

interface Settings2FAProps {
  twoFactorEnabled: boolean;
  onUpdate: () => void;  // Callback para refrescar el estado del usuario
}

const Settings2FA: React.FC<Settings2FAProps> = ({ twoFactorEnabled, onUpdate }) => {
  const { t } = useTranslation();
  const [enable2FAModalVisible, setEnable2FAModalVisible] = useState(false);
  const [disableModalVisible, setDisableModalVisible] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // ==========================================================================
  // HANDLE TOGGLE SWITCH
  // ==========================================================================
  const handleToggle = (checked: boolean) => {
    if (checked) {
      // Enable 2FA
      setEnable2FAModalVisible(true);
    } else {
      // Disable 2FA - show confirmation
      showDisableConfirmation();
    }
  };

  // ==========================================================================
  // SHOW DISABLE CONFIRMATION
  // ==========================================================================
  const showDisableConfirmation = () => {
    confirm({
      title: translate(t, TRANSLATIONS.TWO_FA_DISABLE_CONFIRM_TITLE),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <Paragraph>
            {translate(t, TRANSLATIONS.TWO_FA_DISABLE_CONFIRM_CONTENT)}
          </Paragraph>
        </div>
      ),
      okText: translate(t, TRANSLATIONS.TWO_FA_DISABLE_CONFIRM_OK),
      okType: 'danger',
      cancelText: translate(t, TRANSLATIONS.CANCEL),
      onOk: () => {
        setDisableModalVisible(true);
      },
    });
  };

  // ==========================================================================
  // HANDLE DISABLE 2FA
  // ==========================================================================
  const handleDisable2FA = async () => {
    if (!password.trim()) {
      message.warning(translate(t, TRANSLATIONS.TWO_FA_PASSWORD_REQUIRED_WARNING));
      return;
    }

    setLoading(true);
    try {
      await disable2FA(password);
      message.success(translate(t, TRANSLATIONS.TWO_FA_DISABLED_SUCCESS));
      setDisableModalVisible(false);
      setPassword('');
      onUpdate(); // Refresh user state
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.TWO_FA_DISABLE_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // HANDLE ENABLE SUCCESS
  // ==========================================================================
  const handleEnableSuccess = () => {
    setEnable2FAModalVisible(false);
    onUpdate(); // Refresh user state
  };

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <Text type="secondary">
            {translate(t, TRANSLATIONS.TWO_FA_SETTINGS_SUBTITLE)}
          </Text>
          {/* No checkedChildren/unCheckedChildren text -- the Alert right
              below already says whether 2FA is on/off, and the text labels
              were also forcing a wider, differently-shaped switch than every
              other Switch in the app (SettingsToggleRow's), reading as an
              unrelated control instead of the same toggle. */}
          <Switch checked={twoFactorEnabled} onChange={handleToggle} />
        </div>

        {/* Status Alert */}
        {twoFactorEnabled ? (
          <Alert
            message={translate(t, TRANSLATIONS.TWO_FA_ENABLED_ALERT_MESSAGE)}
            description={translate(t, TRANSLATIONS.TWO_FA_ENABLED_ALERT_DESCRIPTION)}
            type="success"
            showIcon
          />
        ) : (
          <Alert
            message={translate(t, TRANSLATIONS.TWO_FA_DISABLED_ALERT_MESSAGE)}
            description={translate(t, TRANSLATIONS.TWO_FA_DISABLED_ALERT_DESCRIPTION)}
            type="warning"
            showIcon
          />
        )}

        {/* Information */}
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>{translate(t, TRANSLATIONS.TWO_FA_HOW_IT_WORKS_TITLE)}</Text>
          <ul style={{ marginLeft: 20 }}>
            <li>{translate(t, TRANSLATIONS.TWO_FA_HOW_STEP_1)}</li>
            <li>{translate(t, TRANSLATIONS.TWO_FA_HOW_STEP_2)}</li>
            <li>{translate(t, TRANSLATIONS.TWO_FA_HOW_STEP_3)}</li>
            <li>{translate(t, TRANSLATIONS.TWO_FA_HOW_STEP_4)}</li>
            <li>{translate(t, TRANSLATIONS.TWO_FA_HOW_STEP_5)}</li>
          </ul>
        </div>

        {/* Action Button */}
        {!twoFactorEnabled && (
          <Button
            type="primary"
            shape="round"
            icon={<SafetyOutlined />}
            size="large"
            onClick={() => setEnable2FAModalVisible(true)}
          >
            {translate(t, TRANSLATIONS.TWO_FA_ENABLE_BUTTON)}
          </Button>
        )}
      </Space>

      {/* Enable 2FA Modal */}
      <Enable2FAModal
        visible={enable2FAModalVisible}
        onClose={() => setEnable2FAModalVisible(false)}
        onSuccess={handleEnableSuccess}
      />

      {/* Disable 2FA Modal */}
      <Modal
        title={translate(t, TRANSLATIONS.TWO_FA_CONFIRM_PASSWORD_MODAL_TITLE)}
        open={disableModalVisible}
        onOk={handleDisable2FA}
        onCancel={() => {
          setDisableModalVisible(false);
          setPassword('');
        }}
        confirmLoading={loading}
        okText={translate(t, TRANSLATIONS.TWO_FA_DISABLE_BUTTON)}
        okButtonProps={{ danger: true, shape: 'round' }}
        cancelButtonProps={{ shape: 'round' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message={translate(t, TRANSLATIONS.TWO_FA_SECURITY_VERIFICATION_MESSAGE)}
            description={translate(t, TRANSLATIONS.TWO_FA_SECURITY_VERIFICATION_DESCRIPTION)}
            type="warning"
            showIcon
          />

          <Form.Item required>
            <FloatingLabelInput
              label={translate(t, TRANSLATIONS.TWO_FA_PASSWORD_LABEL)}
              password
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onPressEnter={handleDisable2FA}
              disabled={loading}
            />
          </Form.Item>
        </Space>
      </Modal>
    </>
  );
};

export default Settings2FA;